<template>
  <VChart autoresize class="chart" :option="option" />
</template>

<script>
import { defineComponent, computed } from 'vue'
import VChart from 'vue-echarts'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { BarChart } from 'echarts/charts'
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components'
import { detection_zone_palettes } from '@/constants.js'
import { useI18n } from 'vue-i18n'
import { currentLanguage } from '@/i18n'
import { formatNumber } from '@/i18n/format'

use([CanvasRenderer, BarChart, GridComponent, TooltipComponent, LegendComponent])

export default defineComponent({
  name: 'ChartDurationSegmentationCountsPerDay',
  components: { VChart },
  props: {
    durations: { type: Object, required: true },
    timeSpan: { type: String, required: true },
    detectionZoneTypeName: { type: String, required: true }
  },
  setup(props){
    const labels = computed(() => Object.keys(props.durations || {}))
    const zoneName = computed(() => {
      const ls = labels.value
      return ls.length > 0 ? Object.keys(props.durations[ls[0]] || {})[0] : null
    })

    const seriesFor = key =>
      computed(() => labels.value.map(d => (props.durations[d][zoneName.value]?.[key] ?? 0)))

    const walkBys         = seriesFor('walk_bys')
    const shortLingerers  = seriesFor('short_lingerers')
    const clearLingerers  = seriesFor('clear_lingerers')

    const palette = computed(() =>
      detection_zone_palettes[props.detectionZoneTypeName] ?? {
        segmentation: { walkBy:'#B2C149', shortLinger:'#4F7EB3', clearLinger:'#425461' }
      }
    )

    const { t } = useI18n()
    // English keeps its en-GB "Mon 17 Oct"; Dutch reads "ma 17 okt".
    const fmtX = v => {
      const d = new Date(v)
      return d.toLocaleDateString(currentLanguage() === 'en' ? 'en-GB' : 'nl', { weekday:'short', day:'numeric', month:'short' })
    }

    const option = computed(() => ({
      // Dutch numbers read 1.234; English keeps ECharts' own formatting.
      tooltip: currentLanguage() === 'en' ? { trigger:'axis' } : { trigger:'axis', valueFormatter: v => formatNumber(v) },
      legend: { bottom: 0 },
      grid: { bottom:'10%', containLabel:true },
      xAxis: { type:'category', data: labels.value, axisLabel:{ formatter: fmtX, rotate:45 } },
      yAxis: { type:'value', name: t('charts.perDay.visitorCountAxis', { span: props.timeSpan }), nameTextStyle:{ align:'left' },
        ...(currentLanguage() === 'en' ? {} : { axisLabel:{ formatter: v => formatNumber(v) } }) },
      series: [
        { name:t('charts.segments.walkBys'),         type:'bar', stack:'total', data:walkBys.value,        itemStyle:{ color:palette.value.segmentation.walkBy } },
        { name:t('charts.segments.shortLingerers'),  type:'bar', stack:'total', data:shortLingerers.value, itemStyle:{ color:palette.value.segmentation.shortLinger } },
        { name:t('charts.segments.clearLingerers'),  type:'bar', stack:'total', data:clearLingerers.value, itemStyle:{ color:palette.value.segmentation.clearLinger } },
      ]
    }))

    return { option }
  }
})
</script>

<style scoped>
.chart{ width:100%; height:100%; }
</style>